'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { HandsfreeOnboardingStep } from './project-usability-handsfree-onboarding.consts';
import {
  HANDSFREE_ONBOARDING_STEPS,
  HANDSFREE_ONBOARDING_CARD_GAP_PX,
} from './project-usability-handsfree-onboarding.consts';
import type {
  TargetRect,
  CardPosition,
} from './project-usability-handsfree-onboarding.helpers';
import {
  unionRect,
  getCardPosition,
} from './project-usability-handsfree-onboarding.helpers';

export function useOnboardingPosition(
  step: HandsfreeOnboardingStep | undefined
): {
  targetRect: TargetRect | null;
  updateRect: () => void;
} {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const rafRef = useRef<number | null>(null);

  const updateRect = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const mainRect: TargetRect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
    const additionalSelector = step.additionalTargetSelector;
    if (additionalSelector) {
      const additionalEl = document.querySelector(additionalSelector);
      if (additionalEl) {
        const r = additionalEl.getBoundingClientRect();
        setTargetRect(
          unionRect(mainRect, {
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          })
        );
      } else {
        setTargetRect(mainRect);
      }
    } else {
      setTargetRect(mainRect);
    }
  }, [step]);

  useEffect(() => {
    if (step === undefined) return;

    let cancelled = false;
    let frame2Id: number | null = null;

    const runUpdate = () => {
      if (cancelled) return;
      updateRect();
    };

    const frame1Id = requestAnimationFrame(() => {
      if (cancelled) return;
      frame2Id = requestAnimationFrame(runUpdate);
    });

    const timeoutId = setTimeout(runUpdate, 150);
    const stabilizationIntervalId = setInterval(runUpdate, 80);
    const stabilizationClearId = setTimeout(() => {
      clearInterval(stabilizationIntervalId);
    }, 600);

    const observer = new ResizeObserver(() => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (cancelled) return;
        updateRect();
        rafRef.current = null;
      });
    });

    const el = document.querySelector(step.targetSelector);
    if (el) observer.observe(el);

    const additionalEl = step.additionalTargetSelector
      ? document.querySelector(step.additionalTargetSelector)
      : null;
    if (additionalEl) observer.observe(additionalEl);

    window.addEventListener('scroll', updateRect, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', updateRect);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame1Id);
      if (frame2Id !== null) cancelAnimationFrame(frame2Id);
      clearTimeout(timeoutId);
      clearInterval(stabilizationIntervalId);
      clearTimeout(stabilizationClearId);
      observer.disconnect();
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [step, updateRect]);

  useEffect(() => {
    if (!step || targetRect !== null) return;

    const maxAttempts = 10;
    let attempts = 0;

    const id = setInterval(() => {
      attempts += 1;
      updateRect();
      if (attempts >= maxAttempts) clearInterval(id);
    }, 300);

    return () => clearInterval(id);
  }, [targetRect, step, updateRect]);

  return { targetRect, updateRect };
}

export function useOnboardingCardSize(): {
  cardRef: React.RefObject<HTMLDivElement | null>;
  cardSize: { width: number; height: number } | null;
} {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setCardSize({ width: rect.width, height: rect.height });
    };

    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    const frameId = requestAnimationFrame(updateSize);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, []);

  return { cardRef, cardSize };
}

interface UseOnboardingStepReturn {
  step: HandsfreeOnboardingStep | undefined;
  isLastStep: boolean;
  stepNumber: number;
  totalSteps: number;
  hasCloseLine: boolean;
}

export function useOnboardingStep(stepIndex: number): UseOnboardingStepReturn {
  const totalSteps = HANDSFREE_ONBOARDING_STEPS.length;
  const lastIndex = totalSteps - 1;

  const step =
    stepIndex >= 0 && stepIndex <= lastIndex
      ? HANDSFREE_ONBOARDING_STEPS[stepIndex]
      : undefined;

  const isLastStep = stepIndex === lastIndex;
  const stepNumber = stepIndex + 1;
  const hasCloseLine = step?.hasCloseLine ?? false;

  return {
    step,
    isLastStep,
    stepNumber,
    totalSteps,
    hasCloseLine,
  };
}

interface UseOnboardingCardPositionReturn {
  cardPosition: CardPosition | null;
}

export function useOnboardingCardPosition(
  targetRect: TargetRect | null,
  cardSize: { width: number; height: number } | null,
  step: HandsfreeOnboardingStep | undefined
): UseOnboardingCardPositionReturn {
  const cardPosition: CardPosition | null =
    targetRect && cardSize && step
      ? getCardPosition(
          targetRect,
          cardSize,
          step.placement,
          HANDSFREE_ONBOARDING_CARD_GAP_PX
        )
      : null;

  return { cardPosition };
}

interface UseOnboardingHandlersReturn {
  handlePrimary: () => void;
  handleSkip: () => void;
}

export function useOnboardingHandlers(
  isLastStep: boolean,
  onNext: () => void,
  onEnd: () => void
): UseOnboardingHandlersReturn {
  const handlePrimary = () => {
    if (isLastStep) {
      onEnd();
    } else {
      onNext();
    }
  };

  const handleSkip = () => {
    onNext();
  };

  return {
    handlePrimary,
    handleSkip,
  };
}

interface UseOnboardingUIStateOptions {
  isOnboardingActive: boolean;
  currentOnboardingStep: number;
  isPopupOpen: boolean;
  isHelpExpanded: boolean;
}

interface UseOnboardingUIStateReturn {
  effectivePopupOpen: boolean;
  effectiveHelpExpanded: boolean;
  canCloseTaskPanel: boolean;
}

export function useOnboardingUIState({
  isOnboardingActive,
  currentOnboardingStep,
  isPopupOpen,
  isHelpExpanded,
}: UseOnboardingUIStateOptions): UseOnboardingUIStateReturn {
  const lastOnboardingStepIndex = HANDSFREE_ONBOARDING_STEPS.length - 1;

  const effectivePopupOpen =
    isPopupOpen ||
    (isOnboardingActive &&
      (currentOnboardingStep === 0 ||
        currentOnboardingStep === lastOnboardingStepIndex));

  const effectiveHelpExpanded =
    isHelpExpanded || (isOnboardingActive && currentOnboardingStep === 2);

  const canCloseTaskPanel = !(
    isOnboardingActive &&
    (currentOnboardingStep === 0 ||
      currentOnboardingStep === lastOnboardingStepIndex)
  );

  return {
    effectivePopupOpen,
    effectiveHelpExpanded,
    canCloseTaskPanel,
  };
}
